import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type UserTenantWithPerms = {
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  role: string;
  is_primary: boolean;
  can_create_events: boolean;
  can_manage_venues: boolean;
};

export type OrganizerRequest = {
  id: string;
  user_id: string;
  message: string | null;
  status: "pending" | "approved" | "rejected";
  requested_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  admin_notes: string | null;
  granted_tenant_id: string | null;
};

export type AdminUserRow = {
  user_id: string;
  email: string;
  display_name: string | null;
  global_role: string;
  tenant_count: number;
  has_pending_request: boolean;
  created_at: string;
};

export type PendingRequest = {
  request_id: string;
  user_id: string;
  email: string;
  display_name: string | null;
  message: string | null;
  requested_at: string;
};

export async function getUserTenantsWithPerms(userId: string): Promise<UserTenantWithPerms[]> {
  const { data, error } = await supabase.rpc("get_user_tenants_with_perms", { _user_id: userId });
  if (error) throw error;
  return (data ?? []) as UserTenantWithPerms[];
}

export async function isPlatformAdmin(userId: string): Promise<boolean> {
  const { data } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  return !!data;
}

export async function isLegacyOrganizer(userId: string): Promise<boolean> {
  const { data } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "organizer")
    .maybeSingle();
  return !!data;
}

export async function getMyPendingRequest(userId: string): Promise<OrganizerRequest | null> {
  const { data } = await supabase
    .from("organizer_requests")
    .select("*")
    .eq("user_id", userId)
    .order("requested_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  return (data as OrganizerRequest | null) ?? null;
}

export async function submitOrganizerRequest(userId: string, message: string) {
  const { error } = await supabase
    .from("organizer_requests")
    .insert({ user_id: userId, message: message.trim() || null });
  if (error) throw error;
}

export async function listAdminUsers(): Promise<AdminUserRow[]> {
  const { data, error } = await supabase.rpc("list_users_for_admin");
  if (error) throw error;
  return (data ?? []) as AdminUserRow[];
}

export async function listPendingRequests(): Promise<PendingRequest[]> {
  const { data, error } = await supabase.rpc("list_pending_organizer_requests");
  if (error) throw error;
  return (data ?? []) as PendingRequest[];
}

export async function approveRequest(
  requestId: string,
  reviewerId: string,
  opts: { tenantId?: string; role?: string; canCreateEvents?: boolean; canManageVenues?: boolean; notes?: string }
) {
  const { data: req, error: e1 } = await supabase
    .from("organizer_requests")
    .update({
      status: "approved",
      reviewed_at: new Date().toISOString(),
      reviewed_by: reviewerId,
      admin_notes: opts.notes ?? null,
      granted_tenant_id: opts.tenantId ?? null,
    })
    .eq("id", requestId)
    .select("user_id")
    .single();
  if (e1) throw e1;

  // Optional: assign tenant membership
  if (opts.tenantId && req?.user_id) {
    const { error: e2 } = await supabase.from("tenant_memberships").insert({
      user_id: req.user_id,
      tenant_id: opts.tenantId,
      role: opts.role ?? "member",
      can_create_events: opts.canCreateEvents ?? true,
      can_manage_venues: opts.canManageVenues ?? false,
    });
    // Ignore unique-violation if membership already exists
    if (e2 && !e2.message.includes("duplicate")) throw e2;
  }
}

export async function rejectRequest(requestId: string, reviewerId: string, notes: string) {
  const { error } = await supabase
    .from("organizer_requests")
    .update({
      status: "rejected",
      reviewed_at: new Date().toISOString(),
      reviewed_by: reviewerId,
      admin_notes: notes.trim() || null,
    })
    .eq("id", requestId);
  if (error) throw error;
}

export async function assignMembership(
  userId: string,
  tenantId: string,
  role: string,
  canCreateEvents: boolean,
  canManageVenues: boolean
) {
  const { error } = await supabase
    .from("tenant_memberships")
    .upsert(
      {
        user_id: userId,
        tenant_id: tenantId,
        role,
        can_create_events: canCreateEvents,
        can_manage_venues: canManageVenues,
      },
      { onConflict: "user_id,tenant_id" } as any
    );
  if (error) throw error;
}

export async function removeMembership(userId: string, tenantId: string) {
  const { error } = await supabase
    .from("tenant_memberships")
    .delete()
    .eq("user_id", userId)
    .eq("tenant_id", tenantId);
  if (error) throw error;
}

/**
 * Hook: returns the current user's permission state across all their tenants.
 */
export function usePermissions() {
  const { user, loading: authLoading } = useAuth();
  const [tenants, setTenants] = useState<UserTenantWithPerms[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLegacyOrg, setIsLegacyOrg] = useState(false);
  const [pendingRequest, setPendingRequest] = useState<OrganizerRequest | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    async function load() {
      if (authLoading) return;
      if (!user) {
        if (alive) {
          setTenants([]);
          setIsAdmin(false);
          setIsLegacyOrg(false);
          setPendingRequest(null);
          setLoading(false);
        }
        return;
      }
      try {
        const [t, a, o, p] = await Promise.all([
          getUserTenantsWithPerms(user.id),
          isPlatformAdmin(user.id),
          isLegacyOrganizer(user.id),
          getMyPendingRequest(user.id),
        ]);
        if (!alive) return;
        setTenants(t);
        setIsAdmin(a);
        setIsLegacyOrg(o);
        setPendingRequest(p);
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, [user, authLoading]);

  const tenantsWithCreateEvents = tenants.filter((t) => t.can_create_events);
  const tenantsWithVenues = tenants.filter((t) => t.can_manage_venues);

  return {
    user,
    loading: loading || authLoading,
    isPlatformAdmin: isAdmin,
    isLegacyOrganizer: isLegacyOrg,
    tenants,
    tenantsWithCreateEvents,
    tenantsWithVenues,
    canCreateEventsAnywhere: isAdmin || isLegacyOrg || tenantsWithCreateEvents.length > 0,
    canManageVenuesAnywhere: isAdmin || isLegacyOrg || tenantsWithVenues.length > 0,
    belongsToAnyTenant: tenants.length > 0,
    pendingRequest,
    hasPendingRequest: pendingRequest?.status === "pending",
    lastRejection: pendingRequest?.status === "rejected" ? pendingRequest : null,
  };
}
